import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { simpleResultErrorInfo } from "@ryde/shared";

import { MAPLIBRE_PLACE_INFO_STATUS } from "./maplibre-place-info.constants";
import type { IMapLibrePlaceInfoLookup } from "./maplibre-place-info.models";
import {
  canRetryPlaceInfoLookup,
  formatPlaceInfoCoordinate,
  toPlaceInfoDisplay,
} from "./maplibre-place-info.utils";
import { useMapLibrePlaceInfoAddStation } from "./use-maplibre-place-info-add-station";

export const useMapLibrePlaceInfoCard = ({
  lookup,
  onStationAdded,
}: {
  lookup: IMapLibrePlaceInfoLookup;
  onStationAdded: () => void;
}) => {
  const { t } = useTranslation();
  const { canAddStation, isFullscreenKept, addStation } =
    useMapLibrePlaceInfoAddStation();
  const isLoading = lookup.status === MAPLIBRE_PLACE_INFO_STATUS.Loading;
  const isFailed = lookup.status === MAPLIBRE_PLACE_INFO_STATUS.Failed;

  const handleAddStation = useEvent(async (): Promise<void> => {
    const isAdded = await addStation({
      coordinate: lookup.coordinate,
      place: lookup.place,
    });

    if (isAdded) {
      onStationAdded();
    }
  });

  return {
    t,
    display: lookup.place ? toPlaceInfoDisplay(lookup.place) : undefined,
    coordinateText: formatPlaceInfoCoordinate(lookup.coordinate),
    isLoading,
    isEmpty: lookup.status === MAPLIBRE_PLACE_INFO_STATUS.Empty,
    isFailed,
    // The server's own words when it explained the failure, such as a geocoder the company cannot use.
    failureText: lookup.failure
      ? simpleResultErrorInfo(lookup.failure, t)
      : t("ui.PLACE_INFO_ERROR"),
    canRetry: canRetryPlaceInfoLookup(lookup),
    canAddStation,
    // The draft takes the place's name and address, so it waits for them.
    isAddStationDisabled: isLoading,
    isFullscreenKept,
    handleAddStation,
  };
};
